import { useCallback, useEffect, useMemo, useState } from 'react';
import { FaBullseye, FaChartBar, FaLeaf, FaLightbulb } from 'react-icons/fa';
import { IoCheckmarkCircle, IoCloudDownloadOutline, IoTimeOutline } from 'react-icons/io5';
import { MdDelete, MdEditNote } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';

import iniciativaVideoImg from 'assets/img/2026/iniciativa_video.png';

import { VotacaoEtapa2TutorialModal } from 'components/VotacaoEtapa2TutorialModal';
import { useAuth } from 'context/AuthContext';
import { useModal } from 'context/ModalContext/Modal.context';
import { useAvaliacaoEtapa2 } from 'hooks/useAvaliacaoEtapa2';
import { useControlDate } from 'hooks/useControlDate';
import { fetchMinhasAvaliacoesEtapa2 } from 'services/avaliacao-etapa2.service';
import { registrarVotar } from 'services/iniciativa-action.service';
import { getTutorialByTitle } from 'services/tutorial.service';
import { CriterioKey, calcularTotalNotas, isAvaliacaoCompleta } from 'types/etapa2';
import { IniciativaFormatada } from 'types/iniciativa';
import { Tutorial } from 'types/tutorial';
import { PHASE_IDS } from 'utils/constants';
import { getCategoriaLabel, getResultadoLabel } from 'utils/iniciativa-categoria';

import { HeaderElbb } from 'pages/HomeElbb/components';
import './style.scss';

interface SignificadoFaixa {
  range: string;
  descricao: string;
}

interface CriterioDefinicao {
  key: CriterioKey;
  titulo: string;
  descricao: string;
  icone: React.ReactNode;
  faixas: SignificadoFaixa[];
}

const CRITERIOS: CriterioDefinicao[] = [
  {
    key: 'alinhamentoEstrategico',
    titulo: 'Alinhamento Estratégico',
    descricao: 'Avalia o quanto a iniciativa contribui para os direcionadores estratégicos do Banco',
    icone: <FaBullseye size={28} aria-hidden="true" />,
    faixas: [
      { range: '0-2', descricao: 'Não contribui para o direcionador estratégico' },
      { range: '3-4', descricao: 'Contribuição mínima ou indireta' },
      { range: '5-6', descricao: 'Contribuição moderada, mas não essencial' },
      { range: '7-8', descricao: 'Contribuição relevante e alinhada' },
      { range: '9-10', descricao: 'Contribuição essencial e completamente vinculada ao direcionador estratégico' },
    ],
  },
  {
    key: 'impactoMensuravel',
    titulo: 'Impacto Mensurável',
    descricao: 'Considera os resultados concretos obtidos pela iniciativa, com evidências quantitativas ou qualitativas',
    icone: <FaChartBar size={28} aria-hidden="true" />,
    faixas: [
      { range: '0-2', descricao: 'Sem resultados mensuráveis' },
      { range: '3-4', descricao: 'Resultados pouco significativos' },
      { range: '5-6', descricao: 'Resultados moderados, com impacto limitado' },
      { range: '7-8', descricao: 'Resultados relevantes e bem evidenciados' },
      { range: '9-10', descricao: 'Resultados financeiros expressivos, com forte impacto comprovado' },
    ],
  },
  {
    key: 'inovacaoCriatividade',
    titulo: 'Inovação e Criatividade',
    descricao: 'Analisa o grau de originalidade da iniciativa, uso de novas tecnologias ou abordagens diferenciadas',
    icone: <FaLightbulb size={28} aria-hidden="true" />,
    faixas: [
      { range: '0-2', descricao: 'Solução comum, sem inovação' },
      { range: '3-4', descricao: 'Pouca inovação, somente melhorias incrementais' },
      { range: '5-6', descricao: 'Algum grau de inovação, mas não disruptiva' },
      { range: '7-8', descricao: 'Inovação disruptiva em processo ou ambiente complexo' },
      { range: '9-10', descricao: 'Inovação transformacional para o posicionamento do Banco no mercado e na sociedade' },
    ],
  },
  {
    key: 'escalabilidadeSustentabilidade',
    titulo: 'Escalabilidade e Sustentabilidade',
    descricao: 'Verifica se a iniciativa pode ser replicada em outras áreas e se seus resultados são sustentáveis no longo prazo',
    icone: <FaLeaf size={28} aria-hidden="true" />,
    faixas: [
      { range: '0-2', descricao: 'Não escalável nem sustentável' },
      { range: '3-4', descricao: 'Escalabilidade ou sustentabilidade limitada' },
      { range: '5-6', descricao: 'Possível replicação com ajustes' },
      { range: '7-8', descricao: 'Boa escalabilidade e manutenção viável' },
      { range: '9-10', descricao: 'Altamente escalável e sustentável no longo prazo' },
    ],
  },
];

function getSignificado(faixas: SignificadoFaixa[], nota: number): string {
  const faixa = faixas.find(({ range }) => {
    const [start, end] = range.split('-').map(Number);
    return nota >= start && nota <= end;
  });
  return faixa ? `${faixa.range}: ${faixa.descricao}` : '';
}

const STATUS_FILTRO = ['Todas', 'Avaliadas', 'Não avaliadas'];

const CATEGORIA_BADGE_MODIFIER: Record<string, string> = {
  'ECBB Cliente': 'ecbb-cliente',
  'ECBB Cultura': 'ecbb-cultura',
  'ECBB ASG': 'ecbb-asg',
  'Joga Junto': 'joga-junto',
};

function getBadgeModifier(categoria: string): string {
  return CATEGORIA_BADGE_MODIFIER[categoria] ?? 'default';
}

function useCountdown(targetDate: Date) {
  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, targetDate.getTime() - Date.now()));

  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(Math.max(0, targetDate.getTime() - Date.now()));
    }, 1000);
    return () => clearInterval(interval);
  }, [targetDate]);

  const totalSeconds = Math.floor(timeLeft / 1000);
  const days = Math.floor(totalSeconds / 86400);
  const hours = Math.floor((totalSeconds % 86400) / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const format = (n: number) => String(n).padStart(2, '0');
  return `${format(days)}d ${format(hours)}h ${format(minutes)}m ${format(seconds)}s`;
}

const fmtData = new Intl.DateTimeFormat('pt-BR', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
});

export function VotacaoEtapa2() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showModal, closeModal } = useModal();

  const categoriasUsuario = useMemo<string[]>(
    () => {
      const fromUser = (user?.extra?.categories ?? []).filter((c: string) => c === 'ELBB');
      return fromUser.length > 0 ? fromUser : ['ELBB'];
    },
    [user]
  );

  const [categoriaSelecionada, setCategoriaSelecionada] = useState<string | null>(
    () => categoriasUsuario[0] ?? null
  );

  useEffect(() => {
    if (categoriasUsuario.length > 0) {
      setCategoriaSelecionada((prev) => prev ?? categoriasUsuario[0]);
    }
  }, [categoriasUsuario]);

  const [statusFiltro, setStatusFiltro] = useState(STATUS_FILTRO[0]);
  const [busca, setBusca] = useState('');
  const [tutorial, setTutorial] = useState<Tutorial | null>(null);
  const [categoriasVotadas, setCategoriasVotadas] = useState<Set<string>>(
    new Set()
  );

  const recarregarCategoriasVotadas = useCallback(async (): Promise<Set<string>> => {
    if (!user?._id) return new Set();
    try {
      const lista = await fetchMinhasAvaliacoesEtapa2(user._id);
      const novoSet = new Set(lista.map((a) => a.category));
      setCategoriasVotadas(novoSet);
      return novoSet;
    } catch {
      /* sem feedback de erro: marcação verde apenas degrada para ausente */
      return new Set();
    }
  }, [user?._id]);

  useEffect(() => {
    recarregarCategoriasVotadas();
  }, [recarregarCategoriasVotadas]);

  useEffect(() => {
    let canceled = false;
    getTutorialByTitle('TUTORIAL_ETAPA2')
      .then((t) => {
        if (!canceled) setTutorial(t);
      })
      .catch(() => {
        if (!canceled) setTutorial(null);
      });
    return () => {
      canceled = true;
    };
  }, []);

  const controlDate = useControlDate(PHASE_IDS.ETAPA_2_PREMIO);
  const dataInicio = controlDate?.dataInicio ? new Date(controlDate.dataInicio) : null;
  const dataFim = controlDate?.dataFim ? new Date(controlDate.dataFim) : null;
  const targetDate = dataFim ?? new Date();

  const countdown = useCountdown(targetDate);
  const fmt = new Intl.DateTimeFormat('pt-BR', { day: '2-digit', month: '2-digit' });
  const startDateLabel = dataInicio ? fmt.format(dataInicio) : '--';
  const endDateLabel = dataFim ? fmt.format(dataFim) : '--';
  const now = Date.now();
  const totalDuration = dataInicio && dataFim ? dataFim.getTime() - dataInicio.getTime() : 0;
  const elapsed = dataInicio ? now - dataInicio.getTime() : 0;
  const progress = totalDuration > 0 ? Math.min(Math.max((elapsed / totalDuration) * 100, 0), 100) : 0;

  const {
    iniciativas,
    loadingIniciativas,
    erroIniciativas,
    avaliacoes,
    setNota,
    removerAvaliacao,
    podeEnviar,
    totalAvaliadas,
    totalIniciativas,
    enviando,
    enviar,
    erroEnvio,
    avaliacaoAtiva,
    isReadOnly,
    editMode,
    iniciarEdicao,
    cancelarEdicao,
  } = useAvaliacaoEtapa2(categoriaSelecionada);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, []);

  const iniciativasFiltradas = useMemo(() => {
    const termo = busca.trim().toLowerCase();
    return iniciativas.filter((iniciativa) => {
      const notas = avaliacoes[iniciativa.id];
      const completa = isAvaliacaoCompleta(notas);
      let matchStatus = true;
      if (statusFiltro === 'Avaliadas') matchStatus = completa;
      if (statusFiltro === 'Não avaliadas') matchStatus = !completa;
      const matchBusca =
        termo.length === 0 ||
        String(iniciativa.titulo ?? '').toLowerCase().includes(termo);
      return matchStatus && matchBusca;
    });
  }, [busca, statusFiltro, iniciativas, avaliacoes]);

  const abrirTutorial = () => {
    if (!tutorial?.steps?.length) return;
    showModal(
      'votacao-etapa2-tutorial-modal',
      <VotacaoEtapa2TutorialModal
        steps={tutorial.steps}
        onClose={closeModal}
        onStartVoting={closeModal}
      />,
      'votacao-etapa2-tutorial-wrapper',
      false
    );
  };

  const handleEnviarVotos = async () => {
    try {
      await enviar();
      const votadas = await recarregarCategoriasVotadas();
      const todasConcluidas =
        categoriasUsuario.length > 0 &&
        categoriasUsuario.every((c) => votadas.has(c));
      if (todasConcluidas) {
        // Avaliador completou todas as categorias do comitê: confirmação.
        if (user?._id) {
          try {
            await registrarVotar(user._id);
          } catch (actionLogError) {
            console.warn('Falha ao registrar action_log votar (Etapa 2):', actionLogError);
          }
        }
        navigate('/confirmacao-voto-etapa-2');
        return;
      }
      // Ainda há categorias pendentes: rola para o topo para que o avaliador
      // visualize o banner do comitê e selecione a próxima categoria.
      window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    } catch {
      /* erroEnvio já populado pelo hook */
    }
  };

  const dataEnvioFormatada = avaliacaoAtiva
    ? fmtData.format(new Date(avaliacaoAtiva.enviadoEm))
    : null;

  // if (categoriasUsuario.length === 0) {
  //   return (
  //     <div id="votacao-etapa-2-wrapper">
  //       <Header currentPage="avaliacao-etapa-2" />
  //       <main className="votacao-etapa-2-main" style={{ padding: 24 }}>
  //         <h2>Você ainda não tem categorias do comitê atribuídas.</h2>
  //         <p>Procure o suporte se acredita que isso é um erro.</p>
  //         <button onClick={() => navigate('/home-public')}>VOLTAR</button>
  //       </main>
  //     </div>
  //   );
  // }

  return (
    <div id="votacao-etapa-2-wrapper">
      <HeaderElbb currentPage="iniciativas" />

      <section className="votacao-etapa-2-hero">
        <div className="votacao-etapa-2-hero__content">
          <h1 className="votacao-etapa-2-hero__title">Etapa 2 de avaliação</h1>
          <p className="votacao-etapa-2-hero__description">
            Hora de analisar e pontuar! Avalie as iniciativas com base nos critérios e ajude a levar
            as 5 mais bem pontuadas para a próxima fase.
          </p>
          <button
            type="button"
            className="votacao-etapa-2-hero__link"
            onClick={abrirTutorial}
          >
            Saiba aqui como votar!
          </button>

          <div className="votacao-etapa-2-hero__timer">
            <span className="votacao-etapa-2-hero__timer-label">
              O PERÍODO DE VOTAÇÃO SERÁ FINALIZADO EM:
            </span>
            <span className="votacao-etapa-2-hero__timer-badge">
              <IoTimeOutline />
              {countdown}
            </span>
          </div>

          <div className="votacao-etapa-2-hero__progress">
            <span className="votacao-etapa-2-hero__progress-date">De {startDateLabel}</span>
            <div className="votacao-etapa-2-hero__progress-bar">
              <div
                className="votacao-etapa-2-hero__progress-fill"
                style={{ width: `${progress}%` }}
              />
            </div>
            <span className="votacao-etapa-2-hero__progress-date">Até {endDateLabel}</span>
          </div>
        </div>
      </section>

      {(isReadOnly || editMode) && avaliacaoAtiva && dataEnvioFormatada && (
        <div className="px-8 pt-6">
          <div
            className={`max-w-[1200px] mx-auto flex items-start sm:items-center gap-3 sm:gap-4 rounded-xl px-4 sm:px-6 py-3 sm:py-4 border-l-4 shadow-sm ${
              isReadOnly
                ? 'bg-yellow-bb/15 border-yellow-bb'
                : 'bg-blue-bb-6/10 border-blue-bb-6'
            }`}
            role="status"
            aria-live="polite"
          >
            {isReadOnly ? (
              <IoCheckmarkCircle
                className="flex-shrink-0 text-yellow-bb text-2xl sm:text-3xl mt-0.5 sm:mt-0"
                aria-hidden="true"
              />
            ) : (
              <MdEditNote
                className="flex-shrink-0 text-blue-bb-6 text-2xl sm:text-3xl mt-0.5 sm:mt-0"
                aria-hidden="true"
              />
            )}
            <p className="m-0 text-sm sm:text-base text-blue-bb-1 leading-relaxed">
              {isReadOnly ? (
                <>
                  <strong className="font-bold">
                    Avaliação enviada em {dataEnvioFormatada}.
                  </strong>{' '}
                  Clique em <strong className="font-bold">EDITAR AVALIAÇÃO</strong> para alterar suas notas.
                </>
              ) : (
                <>
                  <strong className="font-bold">
                    Editando avaliação enviada em {dataEnvioFormatada}.
                  </strong>{' '}
                  Suas alterações substituirão o envio anterior. Clique em{' '}
                  <strong className="font-bold">CANCELAR</strong> para voltar.
                </>
              )}
            </p>
          </div>
        </div>
      )}

      <main className="votacao-etapa-2-main">
        <div className="votacao-etapa-2-topbar">
          <p className="votacao-etapa-2-topbar__counter">
            Iniciativas avaliadas {totalAvaliadas}/{totalIniciativas}
          </p>
          {isReadOnly ? (
            <button
              type="button"
              className="votacao-etapa-2-topbar__submit"
              onClick={iniciarEdicao}
            >
              EDITAR AVALIAÇÃO
            </button>
          ) : (
            <div className="votacao-etapa-2-topbar__actions">
              {editMode && (
                <button
                  type="button"
                  className="votacao-etapa-2-topbar__cancel"
                  onClick={cancelarEdicao}
                >
                  CANCELAR
                </button>
              )}
              <button
                type="button"
                className="votacao-etapa-2-topbar__submit"
                onClick={handleEnviarVotos}
                disabled={!podeEnviar || enviando}
                aria-disabled={!podeEnviar || enviando}
              >
                {enviando ? 'ENVIANDO…' : 'ENVIAR VOTOS'}
              </button>
            </div>
          )}
        </div>

        {loadingIniciativas && (
          <p className="votacao-etapa-2-list__empty">Carregando iniciativas…</p>
        )}
        {erroIniciativas && !loadingIniciativas && (
          <p className="votacao-etapa-2-list__empty">{erroIniciativas}</p>
        )}
        {erroEnvio && (
          <p className="votacao-etapa-2-list__empty" role="alert">
            {erroEnvio}
          </p>
        )}

        <div className="votacao-etapa-2-filters">
          <select
            value={statusFiltro}
            onChange={(event) => setStatusFiltro(event.target.value)}
            className="votacao-etapa-2-filters__select"
          >
            {STATUS_FILTRO.map((status) => (
              <option key={status} value={status}>
                {status}
              </option>
            ))}
          </select>
          <input
            type="search"
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
            placeholder="O que você procura?"
            className="votacao-etapa-2-filters__search"
          />
        </div>

        <section className="votacao-etapa-2-list">
          {!loadingIniciativas && !erroIniciativas && iniciativasFiltradas.length === 0 && (
            <p className="votacao-etapa-2-list__empty">
              Nenhuma iniciativa encontrada para os filtros selecionados.
            </p>
          )}

          {!loadingIniciativas && !erroIniciativas && iniciativasFiltradas.map((iniciativa: IniciativaFormatada) => {
            const notas = avaliacoes[iniciativa.id];
            const total = calcularTotalNotas(notas);
            const completa = isAvaliacaoCompleta(notas);
            const categoriaLabel = getCategoriaLabel(iniciativa.categoryPremio ?? '');

            return (
              <article
                key={iniciativa.id}
                className={`votacao-etapa-2-card ${completa ? 'is-evaluated' : ''}`}
              >
                <div className="votacao-etapa-2-card__header">
                  <h3 className="votacao-etapa-2-card__title">{iniciativa.titulo}</h3>
                  <span
                    className={`votacao-etapa-2-card__badge votacao-etapa-2-card__badge--${getBadgeModifier(categoriaLabel)}`}
                  >
                    {categoriaLabel}
                  </span>
                </div>

                <div className="votacao-etapa-2-card__content-grid">
                  {(iniciativa.videoUrl || iniciativa.imagem || iniciativa.documentacao) && (
                    <div className="votacao-etapa-2-card__media-col">
                      {iniciativa.videoUrl ? (
                        <div className="votacao-etapa-2-card__video-container">
                          <a
                            href={iniciativa.videoUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="votacao-etapa-2-card__video-link"
                          >
                            <img
                              src={iniciativaVideoImg}
                              alt="Clique para assistir ao vídeo da iniciativa"
                              className="votacao-etapa-2-card__video-thumbnail"
                            />
                          </a>
                        </div>
                      ) : iniciativa.imagem ? (
                        <div className="votacao-etapa-2-card__video-container">
                          <img
                            src={iniciativa.imagem}
                            alt={iniciativa.titulo}
                            className="votacao-etapa-2-card__video-thumbnail"
                          />
                        </div>
                      ) : null}

                      {iniciativa.documentacao && (
                        <div className="votacao-etapa-2-card__documentacao">
                          <p className="votacao-etapa-2-card__documentacao-label">
                            Documentação complementar
                          </p>
                          <button
                            className="votacao-etapa-2-card__download-btn"
                            type="button"
                            onClick={() => window.open(iniciativa.documentacao, '_blank')}
                          >
                            <IoCloudDownloadOutline size={18} aria-hidden="true" />
                            <span>DOWNLOAD</span>
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  <div className="votacao-etapa-2-card__info-col">
                    {(() => {
                      const descricaoExibida =
                        iniciativa.iniciativaOriginal?.description ||
                        iniciativa.descricao ||
                        '';
                      const resultadoOriginalApi = iniciativa.iniciativaOriginal?.result;
                      const resultadoExibido =
                        resultadoOriginalApi || iniciativa.texto || descricaoExibida;
                      const labelResultado = getResultadoLabel(
                        iniciativa.categoryPremio,
                        iniciativa.iniciativaOriginal?.indicator,
                        resultadoOriginalApi || iniciativa.texto
                      );

                      return (
                        <>
                          <div className="votacao-etapa-2-card__descricao">
                            <h4 className="votacao-etapa-2-card__descricao-title">Descrição</h4>
                            <p className="votacao-etapa-2-card__descricao-text">{descricaoExibida}</p>
                          </div>

                          {labelResultado.mostrar && (
                            <div className="votacao-etapa-2-card__resultado">
                              <h4 className="votacao-etapa-2-card__resultado-title">
                                {labelResultado.titulo}
                              </h4>
                              <p className="votacao-etapa-2-card__resultado-text">{resultadoExibido}</p>
                            </div>
                          )}
                        </>
                      );
                    })()}
                  </div>
                </div>

                <div className="votacao-etapa-2-card__evaluation">
                  <h4 className="votacao-etapa-2-card__evaluation-title">Avalie esta iniciativa</h4>
                  <div className="votacao-etapa-2-card__evaluation-header">
                    <p className="votacao-etapa-2-card__evaluation-hint">
                      Pontue cada critério de 0 a 10. Passe o mouse sobre cada nota para ver o significado
                    </p>
                    <span className="votacao-etapa-2-card__evaluation-note">
                      <span className="votacao-etapa-2-card__evaluation-note-icon">i</span>
                      As 5 iniciativas mais bem pontuadas serão selecionadas
                    </span>
                  </div>

                  <div className="votacao-etapa-2-criterios">
                    {CRITERIOS.map((criterio) => {
                      const valor = notas?.[criterio.key as CriterioKey] ?? null;
                      return (
                        <div key={criterio.key} className="votacao-etapa-2-criterio">
                          <div className="votacao-etapa-2-criterio__icon" aria-hidden="true">
                            {criterio.icone}
                          </div>
                          <h5 className="votacao-etapa-2-criterio__title">{criterio.titulo}</h5>
                          <p className="votacao-etapa-2-criterio__description">{criterio.descricao}</p>
                          <div
                            className="votacao-etapa-2-criterio__scale"
                            role="radiogroup"
                            aria-label={`Nota de ${criterio.titulo}`}
                          >
                            {Array.from({ length: 11 }).map((_, nota) => {
                              const isSelected = valor === nota;
                              return (
                                <button
                                  key={nota}
                                  type="button"
                                  role="radio"
                                  aria-checked={isSelected}
                                  disabled={isReadOnly}
                                  className={`votacao-etapa-2-criterio__score ${isSelected ? 'is-selected' : ''}`}
                                  data-tooltip={getSignificado(criterio.faixas, nota)}
                                  onClick={() => {
                                    if (isReadOnly) return;
                                    setNota(iniciativa.id, criterio.key as CriterioKey, nota);
                                  }}
                                >
                                  {nota}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {completa && (
                    <>
                      <div className="votacao-etapa-2-card__evaluation-footer">
                        {!isReadOnly && (
                          <button
                            type="button"
                            className="votacao-etapa-2-card__remove-btn"
                            onClick={() => removerAvaliacao(iniciativa.id)}
                          >
                            REMOVER AVALIAÇÃO
                            <MdDelete size={16} aria-hidden="true" />
                          </button>
                        )}
                      </div>
                      <div className="votacao-etapa-2-card__total">Total: {total} pontos</div>
                    </>
                  )}
                </div>
              </article>
            );
          })}
        </section>

      </main>

      {totalAvaliadas > 0 && !isReadOnly && (
        <aside className="votacao-etapa-2-floating-card" role="status" aria-live="polite">
          <h4>
            Iniciativas Avaliadas {totalAvaliadas}/{totalIniciativas}
          </h4>
          <p>
            {podeEnviar
              ? 'Você avaliou todas as iniciativas. Vá em frente e envie seus votos!'
              : 'Continue avaliando as iniciativas para poder enviar os votos.'}
          </p>
          <div className="votacao-etapa-2-floating-card__actions">
            {editMode && (
              <button
                type="button"
                className="votacao-etapa-2-floating-card__btn-cancelar"
                onClick={cancelarEdicao}
              >
                CANCELAR
              </button>
            )}
            <button
              type="button"
              className="votacao-etapa-2-floating-card__btn-confirmar"
              onClick={handleEnviarVotos}
              disabled={!podeEnviar || enviando}
              aria-disabled={!podeEnviar || enviando}
            >
              {enviando ? 'ENVIANDO…' : 'ENVIAR VOTOS'}
            </button>
          </div>
          {!podeEnviar && totalIniciativas > 0 && (
            <p className="votacao-etapa-2-floating-card__hint">
              Você precisa avaliar todas as iniciativas para enviar seus votos.
              Seu rascunho fica salvo automaticamente.
            </p>
          )}
        </aside>
      )}
    </div>
  );
}
